<template>
  <teleport to="body">
    <div v-if="isOpen" class="modal-backdrop" @click="closeModal">
      <div class="modal fade show" :class="{ show: isOpen }" :style="{ display: isOpen ? 'block' : 'none' }"
        @click.stop>
          <div
              ref="dialogRef"
              class="modal-dialog"
              :class="dialogClass"
              :style="[
                dialogStyle,
                { transform: `translate(${state.x}px, ${state.y}px)` }
              ]"
            >


          <div class="modal-content  custom-modal-content">
            <!-- Slot para el encabezado del modal -->
            <div
              class="modal-drag-zone"
              :class="{'has-header': modalHead, 'drag-disabled': !draggable}"
              @mousedown="startDrag">
              <!-- Slot opcional -->
              <slot name="header" v-if="modalHead"></slot>
            </div>
            <!-- Cuerpo del modal (contenido dinámico) -->
            <div class="modal-body custom-modal-body text-left p-0 scrollable-modal-body">
              <slot name="body"></slot>
            </div>
            <!-- Pie del modal con los botones de acción -->
            <div class="modal-footer custom-modal-footer" v-if="modalFooter">
              <slot name="footer"></slot>
            </div>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script lang="ts" setup>
  import { onMounted, onBeforeUnmount, reactive, ref,computed  } from "vue";
  import { createDraggableModal } from "./useDragrable";

const props = withDefaults(defineProps<{
  isOpen: boolean
  modalFooter: boolean
  modalHead: boolean
  enableDrag?: boolean
  modalWidth?: {
    useCustom: boolean
    className?: string
    widthPx?: number
  }
}>(), {
  enableDrag: true,
  modalWidth: () => ({
    useCustom: false,
    className: 'modal-md',
    widthPx: undefined
  })
})

  const emit = defineEmits<{ (e: 'close'): void }>();
  const dialogRef = ref<HTMLElement | null>(null);
const dialogClass = computed(() => {
  return props.modalWidth?.className
})

const dialogStyle = computed(() => {
  if (!props.modalWidth?.useCustom || !props.modalWidth.widthPx) return {}
  return { width: `${props.modalWidth.widthPx}px` }
})

  const state = reactive({ x: 0, y: 0 });
  const draggable = createDraggableModal(() => dialogRef.value);


  const startDrag = (e: MouseEvent) => {
    if (!props.enableDrag) return
    draggable.start(e)
  }

  const syncPosition = () => {
    if (!props.enableDrag) return
    const pos = draggable.getPosition()
    state.x = pos.x
    state.y = pos.y
  }

  document.addEventListener("mousemove", syncPosition);

  const handleEsc = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      emit('close');
    }
  };

  onMounted(() => document.addEventListener('keydown', handleEsc));
  onBeforeUnmount(() => {
    draggable.stop();
    document.removeEventListener("keydown", handleEsc);
    document.removeEventListener("mousemove", syncPosition);
  });

  const closeModal = () => { emit('close'); };
</script>

<style>
.draggable-header {
  cursor: move;
  user-select: none;
}

.modal-drag-zone {
  cursor: move;
  user-select: none;
}

.modal-drag-zone:not(.has-header) {
  height: 12px;
}

.modal-drag-zone.has-header {
  padding: 0;
}

.modal-drag-zone:hover {
  background: rgba(0, 0, 0, 0.03);
}

.modal-content {
  border-radius: 0.75rem;
  overflow: hidden;
  box-shadow:
    0 10px 25px rgba(0, 0, 0, 0.15),
    0 2px 6px rgba(0, 0, 0, 0.1);
  transition:
    box-shadow 0.2s ease,
    transform 0.1s ease;
}

.modal-content:active {
  box-shadow:
    0 20px 40px rgba(0, 0, 0, 0.25),
    0 6px 12px rgba(0, 0, 0, 0.15);
}

.scrollable-modal-body {
  max-height: calc(100vh - 200px);
  overflow-y: auto;
}

.modal-footer {
  background-color: #fafafa;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  padding: 0.15rem 0.6rem;
  height: 50px;
}

.modal-header {
  padding: 0.15rem 0.6rem;
  height: 50px;
  display: flex;
  align-items: center;
}

.modal-header>* {
  margin: 0;
}
</style>